import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { cn } from '../../../lib/utils.js';
import { CharlieButton } from '../button/charlie-button.js';

type ConfirmOptions = {
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
};

type ConfirmFn = (
  titleOrOptions?: string | ConfirmOptions,
  message?: string,
) => Promise<boolean>;

const CharlieConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * useCharlieConfirm — returns an async `confirm()` function that shows
 * a confirmation dialog and resolves to `true` (confirmed) or `false` (cancelled).
 *
 * Must be used within a `<CharlieConfirmProvider>`.
 *
 * @example
 * ```tsx
 * const confirm = useCharlieConfirm();
 *
 * const handleCancel = async () => {
 *   const ok = await confirm('Stop verification?', 'No data will be shared.');
 *   if (ok) abort();
 * };
 * ```
 */
export function useCharlieConfirm(): ConfirmFn {
  const ctx = useContext(CharlieConfirmContext);
  if (!ctx) {
    throw new Error(
      'useCharlieConfirm must be used within a <CharlieConfirmProvider>',
    );
  }
  return ctx;
}

type PendingConfirm = {
  options: Required<ConfirmOptions>;
  resolve: (value: boolean) => void;
};

/**
 * CharlieConfirmProvider — wraps an app tree and provides a global
 * `confirm()` function via context. Renders a single shared dialog
 * instance that is shown/hidden as needed.
 *
 * Desktop: centered modal dialog.
 * Mobile (< md): bottom sheet that slides up from the bottom.
 *
 * @example
 * ```tsx
 * <CharlieConfirmProvider>
 *   <App />
 * </CharlieConfirmProvider>
 * ```
 */
export function CharlieConfirmProvider({
  children,
  animationDuration = 150,
}: {
  children: React.ReactNode;
  animationDuration?: number;
}) {
  const [open, setOpen] = useState(false);
  const pendingRef = useRef<PendingConfirm | null>(null);
  const [options, setOptions] = useState<Required<ConfirmOptions>>({
    title: 'Are you sure?',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    variant: 'destructive',
  });

  const confirm: ConfirmFn = useCallback((titleOrOptions?, message?) => {
    return new Promise<boolean>((resolve) => {
      const opts: Required<ConfirmOptions> =
        typeof titleOrOptions === 'object'
          ? {
              title: titleOrOptions.title ?? 'Are you sure?',
              message: titleOrOptions.message ?? '',
              confirmLabel: titleOrOptions.confirmLabel ?? 'Confirm',
              cancelLabel: titleOrOptions.cancelLabel ?? 'Cancel',
              variant: titleOrOptions.variant ?? 'destructive',
            }
          : {
              title: titleOrOptions ?? 'Are you sure?',
              message: message ?? '',
              confirmLabel: 'Confirm',
              cancelLabel: 'Cancel',
              variant: 'destructive',
            };

      pendingRef.current = { options: opts, resolve };
      setOptions(opts);
      setOpen(true);
    });
  }, []);

  const handleResult = useCallback(
    (result: boolean) => {
      setOpen(false);
      // Resolve after dialog close animation
      setTimeout(() => {
        pendingRef.current?.resolve(result);
        pendingRef.current = null;
      }, animationDuration);
    },
    [animationDuration],
  );

  return (
    <CharlieConfirmContext.Provider value={confirm}>
      {children}

      <DialogPrimitive.Root
        open={open}
        onOpenChange={(o) => {
          if (!o) handleResult(false);
        }}
      >
        <DialogPrimitive.Portal>
          {/* Overlay */}
          <DialogPrimitive.Overlay
            data-slot="confirm-overlay"
            className={cn(
              'fixed inset-0 z-50 bg-black/60',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            )}
          />

          {/* Single responsive content — centered on md+, bottom sheet on mobile */}
          <DialogPrimitive.Content
            data-slot="confirm-content"
            className={cn(
              'fixed z-50 w-full bg-background shadow-lg outline-none',
              // Mobile: bottom sheet
              'inset-x-0 bottom-0 rounded-t-2xl border-t border-border',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=open]:duration-300',
              'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=closed]:duration-200',
              // Desktop: centered dialog
              'md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2',
              'md:max-w-md md:rounded-2xl md:border md:border-border',
              'md:data-[state=open]:slide-in-from-bottom-0 md:data-[state=open]:zoom-in-95 md:data-[state=open]:fade-in-0',
              'md:data-[state=closed]:slide-out-to-bottom-0 md:data-[state=closed]:zoom-out-95 md:data-[state=closed]:fade-out-0',
            )}
          >
            {/* Mobile drag handle */}
            <div className="flex justify-center pt-3 pb-1 md:hidden">
              <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
            </div>

            <div className="flex flex-col gap-6 px-6 pb-8 pt-4 md:p-8">
              {/* Body */}
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="flex items-center justify-center size-12 rounded-full bg-amber-100">
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="size-6 text-amber-600"
                  />
                </div>
                <DialogPrimitive.Title className="text-xl font-semibold text-foreground">
                  {options.title}
                </DialogPrimitive.Title>
                {options.message && (
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {options.message}
                  </DialogPrimitive.Description>
                )}
              </div>

              {/* Actions — stacked on mobile, side-by-side on desktop */}
              <div className="flex flex-col gap-3 w-full md:flex-row">
                <CharlieButton
                  variant="outline"
                  size="lg"
                  pill
                  block
                  className="md:flex-1 md:rounded-md md:h-9 md:px-4 md:py-2 md:text-sm"
                  onClick={() => handleResult(false)}
                >
                  {options.cancelLabel}
                </CharlieButton>
                <CharlieButton
                  variant={
                    options.variant === 'destructive'
                      ? 'destructive'
                      : 'default'
                  }
                  size="lg"
                  pill
                  block
                  className="md:flex-1 md:rounded-md md:h-9 md:px-4 md:py-2 md:text-sm order-first md:order-last"
                  onClick={() => handleResult(true)}
                >
                  {options.confirmLabel}
                </CharlieButton>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </CharlieConfirmContext.Provider>
  );
}
